import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusBadge } from '@/components/base/StatCard';
import { financeTransactions, dayCarryOvers, getCarryOver, getClosingBalance, type FinanceTransaction, type AccountType, currencySymbols, currencyList, type CurrencyCode } from '@/mocks/cash';

// Toast
interface Toast { id: number; message: string; type: 'success' | 'error'; }

export default function FinansPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState<string>('today');
  const [accountFilter, setAccountFilter] = useState<string>('all');
  const [txList, setTxList] = useState<FinanceTransaction[]>(financeTransactions);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const todayStr = '2026-05-09';

  const filtered = useMemo(() => {
    let data = [...txList];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(t =>
        (t.customerName?.toLowerCase().includes(q) || '') ||
        t.description.toLowerCase().includes(q) ||
        (t.notes?.toLowerCase().includes(q) || '')
      );
    }
    if (accountFilter !== 'all') data = data.filter(t => t.account === accountFilter);
    if (dateFilter === 'today') data = data.filter(t => t.date === todayStr);
    if (dateFilter === 'yesterday') data = data.filter(t => t.date === '2026-05-08');
    if (dateFilter === 'week') data = data.filter(t => t.date >= '2026-05-03');
    if (dateFilter === 'month') data = data.filter(t => t.date >= '2026-05-01');
    return data.sort((a, b) => `${b.date}T${b.time}`.localeCompare(`${a.date}T${a.time}`));
  }, [txList, searchQuery, accountFilter, dateFilter]);

  const cashStats = useMemo(() => {
    const txs = txList.filter(t => t.account === 'cash' && t.date === todayStr);
    const opening = getCarryOver(todayStr, 'cash');
    const income = txs.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
    const expense = txs.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
    return { opening, income, expense, closing: opening + income - expense, count: txs.length };
  }, [txList]);

  const bankStats = useMemo(() => {
    const txs = txList.filter(t => t.account === 'bank' && t.date === todayStr);
    const opening = getCarryOver(todayStr, 'bank');
    const income = txs.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
    const expense = txs.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
    return { opening, income, expense, closing: opening + income - expense, count: txs.length };
  }, [txList]);

  const cashMultiStats = useMemo(() => {
    const currencies: CurrencyCode[] = ['TRY', 'EUR', 'USD'];
    return currencies.map(curr => {
      const txs = txList.filter(t => t.account === 'cash' && t.date === todayStr && (t.currency || 'TRY') === curr);
      const income = txs.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
      const expense = txs.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
      const opening = curr === 'TRY' ? getCarryOver(todayStr, 'cash') : 0;
      return { currency: curr, symbol: currencySymbols[curr], opening, income, expense, closing: opening + income - expense, hasData: income > 0 || expense > 0 || opening > 0 };
    }).filter(r => r.hasData);
  }, [txList]);

  const bankMultiStats = useMemo(() => {
    const currencies: CurrencyCode[] = ['TRY', 'EUR', 'USD'];
    return currencies.map(curr => {
      const txs = txList.filter(t => t.account === 'bank' && t.date === todayStr && (t.currency || 'TRY') === curr);
      const income = txs.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
      const expense = txs.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
      const opening = curr === 'TRY' ? getCarryOver(todayStr, 'bank') : 0;
      return { currency: curr, symbol: currencySymbols[curr], opening, income, expense, closing: opening + income - expense, hasData: income > 0 || expense > 0 || opening > 0 };
    }).filter(r => r.hasData);
  }, [txList]);

  const showToast = (message: string, type: 'success' | 'error') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  };

  const handleDelete = (id: string) => {
    if (confirm('Bu işlemi silmek istediğinize emin misiniz?')) {
      setTxList(prev => prev.filter(t => t.id !== id));
      showToast('İşlem silindi.', 'success');
    }
  };

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map(t => (
          <div key={t.id} className="px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 animate-slide-in" style={{ background: t.type === 'success' ? '#10B981' : '#EF4444', color: '#fff' }}>
            <div className="w-4 h-4 flex items-center justify-center"><i className={t.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} /></div>
            {t.message}
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>Finans Yönetimi</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Nakit kasa ve banka hesapları genel bakış</p>
        </div>
      </div>

      {/* Nakit & Banka Genel Bakış Kartları */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Nakit Kasa */}
        <div className="spa-card p-4 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => navigate('/finans/nakit')}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-spa flex items-center justify-center" style={{ background: 'rgba(201,169,98,0.12)' }}>
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-money-cny-circle-line text-lg" style={{ color: '#C9A962' }} /></div>
              </div>
              <div>
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Nakit Kasa</h3>
                <p className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>Bugün — {cashStats.count} hareket</p>
              </div>
            </div>
            <div className="w-6 h-6 flex items-center justify-center"><i className="ri-arrow-right-s-line" style={{ color: 'var(--text-muted)' }} /></div>
          </div>
          <div className="space-y-2">
            {cashMultiStats.map((row, i) => (
              <div key={row.currency} className="rounded-lg px-3 py-2" style={{ background: i === 0 ? 'var(--bg-tertiary)' : 'transparent', border: i > 0 ? '1px solid var(--border-color)' : 'none' }}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{
                    background: row.currency === 'TRY' ? 'rgba(201,169,98,0.15)' : row.currency === 'EUR' ? 'rgba(16,185,129,0.15)' : 'rgba(59,130,246,0.15)',
                    color: row.currency === 'TRY' ? '#C9A962' : row.currency === 'EUR' ? '#10B981' : '#3B82F6'
                  }}>{row.symbol} {row.currency}</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div className="text-center">
                    <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Devir</p>
                    <p className="text-xs font-bold font-display mt-0.5" style={{ color: 'var(--text-primary)' }}>{row.symbol}{row.opening.toLocaleString('tr-TR', { maximumFractionDigits: row.currency === 'TRY' ? 0 : 2 })}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Gelir</p>
                    <p className="text-xs font-bold font-display mt-0.5" style={{ color: '#10B981' }}>{row.symbol}{row.income.toLocaleString('tr-TR', { maximumFractionDigits: row.currency === 'TRY' ? 0 : 2 })}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Gider</p>
                    <p className="text-xs font-bold font-display mt-0.5" style={{ color: '#EF4444' }}>{row.symbol}{row.expense.toLocaleString('tr-TR', { maximumFractionDigits: row.currency === 'TRY' ? 0 : 2 })}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Kapanış</p>
                    <p className="text-xs font-bold font-display mt-0.5" style={{ color: '#2D5F3E' }}>{row.symbol}{row.closing.toLocaleString('tr-TR', { maximumFractionDigits: row.currency === 'TRY' ? 0 : 2 })}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Banka Hesabı */}
        <div className="spa-card p-4 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => navigate('/finans/banka')}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-spa flex items-center justify-center" style={{ background: 'rgba(59,130,246,0.12)' }}>
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-bank-line text-lg" style={{ color: '#3B82F6' }} /></div>
              </div>
              <div>
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Banka Hesabı</h3>
                <p className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>Bugün — {bankStats.count} hareket</p>
              </div>
            </div>
            <div className="w-6 h-6 flex items-center justify-center"><i className="ri-arrow-right-s-line" style={{ color: 'var(--text-muted)' }} /></div>
          </div>
          <div className="space-y-2">
            {bankMultiStats.map((row, i) => (
              <div key={row.currency} className="rounded-lg px-3 py-2" style={{ background: i === 0 ? 'var(--bg-tertiary)' : 'transparent', border: i > 0 ? '1px solid var(--border-color)' : 'none' }}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{
                    background: row.currency === 'TRY' ? 'rgba(201,169,98,0.15)' : row.currency === 'EUR' ? 'rgba(16,185,129,0.15)' : 'rgba(59,130,246,0.15)',
                    color: row.currency === 'TRY' ? '#C9A962' : row.currency === 'EUR' ? '#10B981' : '#3B82F6'
                  }}>{row.symbol} {row.currency}</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div className="text-center">
                    <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Devir</p>
                    <p className="text-xs font-bold font-display mt-0.5" style={{ color: 'var(--text-primary)' }}>{row.symbol}{row.opening.toLocaleString('tr-TR', { maximumFractionDigits: row.currency === 'TRY' ? 0 : 2 })}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Gelir</p>
                    <p className="text-xs font-bold font-display mt-0.5" style={{ color: '#10B981' }}>{row.symbol}{row.income.toLocaleString('tr-TR', { maximumFractionDigits: row.currency === 'TRY' ? 0 : 2 })}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Gider</p>
                    <p className="text-xs font-bold font-display mt-0.5" style={{ color: '#EF4444' }}>{row.symbol}{row.expense.toLocaleString('tr-TR', { maximumFractionDigits: row.currency === 'TRY' ? 0 : 2 })}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Kapanış</p>
                    <p className="text-xs font-bold font-display mt-0.5" style={{ color: '#2D5F3E' }}>{row.symbol}{row.closing.toLocaleString('tr-TR', { maximumFractionDigits: row.currency === 'TRY' ? 0 : 2 })}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Banka Bazlı Özet */}
      <div className="spa-card p-4">
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Banka Bazlı Bakiyeler</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {['Ziraat Bankası','Garanti Bankası','İş Bankası','Akbank','Yapı Kredi','Denizbank'].map(bank => {
            const bankTxs = txList.filter(t => t.account === 'bank' && t.bankName === bank && t.date === todayStr);
            const inc = bankTxs.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
            const exp = bankTxs.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
            return (
              <div key={bank} className="p-3 rounded-lg" style={{ background: 'var(--bg-tertiary)' }}>
                <p className="text-[10px] font-medium truncate" style={{ color: 'var(--text-muted)' }}>{bank}</p>
                <p className="text-sm font-bold font-display mt-1" style={{ color: inc >= exp ? '#10B981' : '#EF4444' }}>₺{(inc - exp).toLocaleString('tr-TR')}</p>
                <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-secondary)' }}>{bankTxs.length} hareket</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-search-line text-sm" style={{ color: 'var(--text-muted)' }} /></div>
          <input className="spa-input pl-10 text-sm" placeholder="Müşteri, açıklama veya not ara..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
        <div className="flex gap-2 flex-wrap">
          <select className="spa-input text-sm min-w-[120px]" value={accountFilter} onChange={e => setAccountFilter(e.target.value)}>
            <option value="all">Tüm Hesaplar</option>
            <option value="cash">Nakit Kasa</option>
            <option value="bank">Banka Hesabı</option>
          </select>
        <div className="flex gap-1 p-1 rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
          {[
            { key: 'all', label: 'Tümü' },
            { key: 'today', label: 'Bugün' },
            { key: 'yesterday', label: 'Dün' },
            { key: 'week', label: 'Bu Hafta' },
            { key: 'month', label: 'Bu Ay' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setDateFilter(f.key)}
              className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all"
              style={{
                background: dateFilter === f.key
                  ? 'linear-gradient(135deg, var(--gold), var(--gold-dark))'
                  : 'transparent',
                color: dateFilter === f.key ? '#fff' : 'var(--text-secondary)',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="spa-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                {['Tarih','Saat','Açıklama','Hesap','Tür','Kasaya Giren','Hizmet Bedeli','Ödeme','Banka','İşlemler'].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs font-semibold whitespace-nowrap" style={{ color: 'var(--text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(tx => (
                <tr key={tx.id} className="border-b hover:bg-theme-hover transition-colors" style={{ borderColor: 'var(--border-color)' }}>
                  <td className="px-3 py-3 whitespace-nowrap text-xs" style={{ color: 'var(--text-secondary)' }}>{tx.date}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs" style={{ color: 'var(--text-muted)' }}>{tx.time}</td>
                  <td className="px-3 py-3">
                    <p style={{ color: 'var(--text-primary)' }}>{tx.description}</p>
                    {tx.customerName && <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{tx.customerName}</p>}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className="spa-badge text-[10px]" style={{ background: tx.account === 'cash' ? 'rgba(201,169,98,0.12)' : 'rgba(59,130,246,0.12)', color: tx.account === 'cash' ? '#C9A962' : '#3B82F6' }}>
                      {tx.account === 'cash' ? 'Nakit' : 'Banka'}
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <StatusBadge status={tx.type === 'income' ? 'Kapalı' : 'Açık'}>{tx.type === 'income' ? 'Gelir' : 'Gider'}</StatusBadge>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap font-semibold font-display" style={{ color: tx.type === 'income' ? '#10B981' : '#EF4444' }}>
                    {tx.type === 'income' ? '+' : '-'}{currencySymbols[tx.currency || 'TRY']}{tx.amount.toLocaleString('tr-TR')}
                    {tx.currency !== 'TRY' && <span className="text-[10px] ml-1 font-normal" style={{ color: 'var(--text-muted)' }}>{tx.currency}</span>}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {tx.serviceAmount && tx.serviceAmount !== tx.amount ? (
                      <span className="font-medium font-display text-xs" style={{ color: 'var(--text-primary)' }}>
                        {currencySymbols[tx.serviceCurrency || 'TRY']}{tx.serviceAmount.toLocaleString('tr-TR')}
                        {tx.serviceCurrency && tx.serviceCurrency !== 'TRY' && <span className="text-[10px] ml-1" style={{ color: 'var(--text-muted)' }}>{tx.serviceCurrency}</span>}
                      </span>
                    ) : (
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>-</span>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {tx.paymentSplits && tx.paymentSplits.length > 0 ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: 'rgba(201,169,98,0.12)', color: '#C9A962' }}>
                        <i className="ri-funds-line" />
                        Parçalı ({tx.paymentSplits.length})
                      </span>
                    ) : tx.paymentType}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs" style={{ color: 'var(--text-muted)' }}>{tx.bankName || '-'}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleDelete(tx.id)} className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer" style={{ background: 'rgba(239,68,68,0.12)' }} title="Sil"><i className="ri-delete-bin-line text-xs" style={{ color: '#EF4444' }} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 space-y-3">
          <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
            <i className="ri-safe-2-line text-xl" style={{ color: 'var(--text-muted)' }} />
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>İşlem bulunamadı</p>
        </div>
      )}
    </div>
  );
}